import { SeamlessApiClient } from "@mondaydotcomorg/api";

const mondayApi = new SeamlessApiClient();
const ITEMS_FETCH_LIMIT = 500;
const unwrapResponse = (response) =>
  response?.data?.data ?? response?.data ?? response;

const BOARD_ITEMS_QUERY = `
  query getBoardItems($boardIds: [ID!], $limit: Int!, $cursor: String) {
    boards(ids: $boardIds) {
      id
      items_page(limit: $limit, cursor: $cursor) {
        cursor
        items {
          id
          name
          group {
            id
          }
          column_values {
            id
            type
            text
            value
          }
        }
      }
    }
  }
`;

const MULTI_VALUE_TYPES = new Set(["dropdown", "tags"]);
const NUMERIC_COLUMN_TYPES = new Set(["numeric", "numbers"]);
const DATE_COLUMN_TYPES = new Set(["date"]);

const executeGraphQl = async (query, variables) => {
  const response = await mondayApi.request(query, variables);
  const data = unwrapResponse(response);

  if (!data) {
    throw new Error("The monday.com API returned no data.");
  }

  return data;
};

const safeJsonParse = (value) => {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const normalizeString = (value = "") => value.toString().trim().toLowerCase();

const getNumericValueFromColumn = (columnValue) => {
  const parsedValue = safeJsonParse(columnValue?.value);
  const raw =
    parsedValue?.value ??
    parsedValue?.numeric_value ??
    columnValue?.text;

  if (raw === undefined || raw === null) {
    return null;
  }

  const sanitized = typeof raw === "string" ? raw.replace(/,/g, "") : raw;
  const numericValue = Number(sanitized);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const getDateValueFromColumn = (columnValue) => {
  const parsedValue = safeJsonParse(columnValue?.value);
  const raw =
    parsedValue?.date ??
    parsedValue?.value ??
    columnValue?.text;

  if (!raw) {
    return null;
  }

  const timestamp = Date.parse(raw);
  return Number.isFinite(timestamp) ? timestamp : null;
};

const buildComparableValue = (columnValue) => {
  const type = columnValue?.type;
  const text = normalizeString(columnValue?.text || "");

  if (MULTI_VALUE_TYPES.has(type) && text) {
    const tokens = text
      .split(",")
      .map((token) => normalizeString(token))
      .filter(Boolean);

    return { text, tokens };
  }

  return { text, tokens: [] };
};

const evaluateStringOperator = (source, target, operator) => {
  switch (operator) {
    case "eq":
      return source === target;
    case "neq":
      return source !== target;
    case "contains":
      return source.includes(target);
    case "not_contains":
      return !source.includes(target);
    case "starts_with":
      return source.startsWith(target);
    case "ends_with":
      return source.endsWith(target);
    default:
      return false;
  }
};

const evaluateComparatorOperator = (source, target, operator) => {
  switch (operator) {
    case "eq":
      return source === target;
    case "neq":
      return source !== target;
    case "gt":
      return source > target;
    case "gte":
      return source >= target;
    case "lt":
      return source < target;
    case "lte":
      return source <= target;
    default:
      return false;
  }
};

const evaluateMultiValueOperator = (tokens, target, operator) => {
  switch (operator) {
    case "eq":
      return tokens.some((token) => token === target);
    case "neq":
      return tokens.every((token) => token !== target);
    case "contains":
      return tokens.some((token) => token.includes(target));
    case "not_contains":
      return tokens.every((token) => !token.includes(target));
    case "starts_with":
    case "ends_with":
      return evaluateStringOperator(tokens.join(","), target, operator);
    default:
      return false;
  }
};

const evaluateFilterForItem = (item, filter) => {
  if (!filter?.columnId) {
    return true;
  }

  const columnValue = item.column_values?.find((column) => column.id === filter.columnId);
  if (!columnValue) {
    return false;
  }

  const operator = filter.operator || "eq";
  const rawValue = (filter.value ?? "").toString().trim();
  if (!rawValue) {
    return true;
  }

  const columnType = columnValue?.type;
  if (NUMERIC_COLUMN_TYPES.has(columnType)) {
    const targetNumber = Number(rawValue.replace(/,/g, ""));
    if (!Number.isFinite(targetNumber)) {
      return false;
    }
    const sourceNumber = getNumericValueFromColumn(columnValue);
    if (!Number.isFinite(sourceNumber)) {
      return false;
    }
    return evaluateComparatorOperator(sourceNumber, targetNumber, operator);
  }

  if (DATE_COLUMN_TYPES.has(columnType)) {
    const targetTimestamp = Date.parse(rawValue);
    if (!Number.isFinite(targetTimestamp)) {
      return false;
    }
    const sourceTimestamp = getDateValueFromColumn(columnValue);
    if (!Number.isFinite(sourceTimestamp)) {
      return false;
    }
    return evaluateComparatorOperator(sourceTimestamp, targetTimestamp, operator);
  }

  const targetValue = normalizeString(rawValue);
  if (!targetValue) {
    return true;
  }

  const { text, tokens } = buildComparableValue(columnValue);
  if (!text && !tokens.length) {
    return false;
  }

  if (tokens.length) {
    return evaluateMultiValueOperator(tokens, targetValue, operator);
  }

  return evaluateStringOperator(text, targetValue, operator);
};

const shuffleItems = (items) => {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

export const pickRandomItems = async ({
  boardId,
  groupIds = [],
  filters = [],
  itemsToSelect = 1
}) => {
  if (!boardId) {
    throw new Error("Board context is required to draw items.");
  }

  let cursor = null;
  const aggregatedItems = [];

  do {
    const data = await executeGraphQl(BOARD_ITEMS_QUERY, {
      boardIds: [boardId],
      limit: ITEMS_FETCH_LIMIT,
      cursor
    });

    const page = data?.boards?.[0]?.items_page;
    const items =
      page?.items?.map((item) => ({
        id: item.id,
        name: item.name,
        groupId: item.group?.id,
        column_values: item.column_values || []
      })) || [];

    aggregatedItems.push(...items);
    cursor = page?.cursor && items.length ? page.cursor : null;
  } while (cursor);

  const items = aggregatedItems;

  const normalizedGroupIds = Array.isArray(groupIds) ? groupIds.filter(Boolean) : [];
  const filteredByGroup = normalizedGroupIds.length
    ? items.filter((item) => normalizedGroupIds.includes(item.groupId))
    : items;

  const normalizedFilters = Array.isArray(filters) ? filters : [];
  const fullyFiltered = normalizedFilters.length
    ? filteredByGroup.filter((item) =>
        normalizedFilters.every((filter) => evaluateFilterForItem(item, filter))
      )
    : filteredByGroup;

  const shuffled = shuffleItems(fullyFiltered);
  return shuffled.slice(0, itemsToSelect);
};

